import { dayjs } from '@koob/dates';

// Mirrors the API fallback (ExperienceCancelConditionsRepository, KD-3881):
// a stay that no rule of the experience overlaps follows its organization's
// rules. ponytail: works in whole UTC days; a stay straddling a gap and an
// own rule follows the own rule in the API but shows here under the gap too.

const ruleDay = (value) => dayjs(value).tz('UTC').format('YYYY-MM-DD');
const periodDay = (value) => dayjs(value).format('YYYY-MM-DD');
const shift = (day, days) => dayjs(day).add(days, 'day').format('YYYY-MM-DD');

const mergeRanges = (ranges) =>
  [...ranges]
    .sort((a, b) => a.from.localeCompare(b.from))
    .reduce((merged, range) => {
      const last = merged[merged.length - 1];
      if (last && range.from <= shift(last.to, 1)) {
        if (range.to > last.to) last.to = range.to;
      } else {
        merged.push({ ...range });
      }
      return merged;
    }, []);

const subtract = (range, covered) => {
  const gaps = [];
  let cursor = range.from;
  for (const rule of covered) {
    if (rule.to < cursor || rule.from > range.to) continue;
    if (rule.from > cursor)
      gaps.push({ from: cursor, to: shift(rule.from, -1) });
    if (rule.to >= range.to) return gaps;
    cursor = shift(rule.to, 1);
  }
  gaps.push({ from: cursor, to: range.to });
  return gaps;
};

/**
 * The organization rules that apply to the experience, grouped by the
 * availability dates its own rules leave uncovered. `ownRules` are form
 * values ({ period: [start, end] }), `periods` the availability periods
 * ({ startAt, endAt }), `orgRules` the API's ({ startAt, endAt, ... }).
 */
export function getOrgFallback({ ownRules, periods, orgRules }) {
  const rules = (orgRules ?? []).map((rule) => ({
    ...rule,
    from: ruleDay(rule.startAt),
    to: ruleDay(rule.endAt),
  }));
  const covered = mergeRanges(
    (ownRules ?? [])
      .filter(({ period }) => period?.[0] && period?.[1])
      .map(({ period }) => ({
        from: ruleDay(period[0]),
        to: ruleDay(period[1]),
      })),
  );
  const available = mergeRanges(
    (periods ?? [])
      .filter(({ startAt, endAt }) => startAt && endAt)
      .map(({ startAt, endAt }) => ({
        from: periodDay(startAt),
        to: periodDay(endAt),
      })),
  );

  if (available.length === 0) {
    return covered.length === 0 && rules.length > 0
      ? [{ from: null, to: null, rules }]
      : [];
  }

  return available
    .flatMap((range) => subtract(range, covered))
    .map((gap) => ({
      ...gap,
      rules: rules.filter((rule) => rule.from <= gap.to && rule.to >= gap.from),
    }))
    .filter((gap) => gap.rules.length > 0);
}
